/**
 * Runaway-render detector.
 *
 * A template-bound getter is re-evaluated on EVERY change-detection pass. If
 * such a getter also returns a fresh array/object each call, anything
 * downstream that diffs by reference (PrimeNG's p-table, *ngFor without
 * trackBy, a chart component's @Input) sees "new" data every pass and
 * re-renders — which can itself schedule another pass. The result is a tab
 * pinned at 100% CPU with an empty console and no network activity: the
 * classic silent "hang".
 *
 * Dev mode would usually surface the underlying instability as
 * ExpressionChangedAfterItHasBeenChecked, but `enableProdMode()` turns that
 * check off — so this is exactly the class of bug that reproduces in
 * production and nowhere else.
 *
 * Usage — call at the top of a suspect getter or in ngDoCheck:
 *
 *   get chartTableData() {
 *     countRender('detail:chartTableData');
 *     ...
 *   }
 *
 * Costs one integer increment per call in the common case.
 */

interface Window_ { count: number; windowStart: number; reported: number; }

const counters = new Map<string, Window_>();

/** Calls per second above which a label is considered to be thrashing. */
const THRESHOLD_PER_SEC = 200;
/** Stop logging a given label after this many reports, so the console stays readable. */
const MAX_REPORTS = 5;
const WINDOW_MS = 1000;

/**
 * Records one invocation of `label`, and warns once per second while the rate
 * stays above THRESHOLD_PER_SEC. Safe to leave in place: it never throws, and
 * a healthy call rate never logs anything at all.
 */
export function countRender(label: string): void {
  try {
    const now = Date.now();
    let entry = counters.get(label);

    if (!entry) {
      entry = { count: 0, windowStart: now, reported: 0 };
      counters.set(label, entry);
    }

    entry.count++;

    const elapsed = now - entry.windowStart;
    if (elapsed < WINDOW_MS) {
      return;
    }

    const perSec = Math.round((entry.count / elapsed) * 1000);
    if (perSec >= THRESHOLD_PER_SEC && entry.reported < MAX_REPORTS) {
      entry.reported++;
      console.warn(
        `[RenderDiag] "${label}" evaluated ~${perSec}x/sec ` +
        `(${entry.count} calls in ${elapsed}ms). This indicates a change-detection loop — ` +
        `the value is very likely a getter returning a new reference each pass.` +
        (entry.reported === MAX_REPORTS ? ' Further reports for this label suppressed.' : '')
      );
    }

    entry.count = 0;
    entry.windowStart = now;
  } catch {
    /* a diagnostic must never break the thing it is measuring */
  }
}

/** Current per-label totals — handy to inspect from the console mid-hang. */
export function renderDiagnosticsSnapshot(): Record<string, number> {
  const out: Record<string, number> = {};
  counters.forEach((v, k) => { out[k] = v.count; });
  return out;
}
